import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Tag } from "antd";
import { BellOutlined, CameraOutlined, SafetyCertificateOutlined, MobileOutlined } from "@ant-design/icons";
import { getRoles } from "../utils/PermissionUtil";
import { isIOS, isStandalone } from "../utils/pwaInstall";
import { getPushPermission, isPushSupported, subscribePush } from "../utils/pushNotifications";

// Đánh dấu đã hiện màn xin quyền trên thiết bị này (bấm "Cho phép" hoặc "Để sau")
const DONE_KEY = "perm_onboarding_done_v1";
// Mỗi phiên chỉ tự đăng ký lại thông báo 1 lần
const SYNC_KEY = "push_synced_session";

const CAMERA_ROLES = ["admin", "manager", "coach"];

const getCameraPermission = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return "unsupported";
    try {
        const status = await navigator.permissions.query({ name: "camera" });
        return status.state; // granted | denied | prompt
    } catch {
        return "prompt"; // Trình duyệt không hỗ trợ kiểm tra trước (vd Firefox)
    }
};

const readStorage = (storage, key) => {
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (storage, key, value) => {
    try {
        storage.setItem(key, value);
    } catch {
        // bỏ qua: chế độ ẩn danh / bị chặn lưu trữ
    }
};

const StatusTag = ({ status }) => {
    const map = {
        granted: <Tag color="success">Đã cho phép</Tag>,
        denied: <Tag color="error">Đang bị chặn</Tag>,
        prompt: <Tag color="warning">Chưa cho phép</Tag>,
        default: <Tag color="warning">Chưa cho phép</Tag>,
        unsupported: <Tag>Không hỗ trợ</Tag>,
        "need-install": <Tag color="processing">Cần cài app</Tag>,
    };
    return map[status] || null;
};

const PermissionItem = ({ icon, title, status, children }) => (
    <div style={{ display: "flex", gap: 12, padding: "14px 0", borderBottom: "1px solid #f1f5f9" }}>
        <div
            style={{
                width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                background: "#fff7ed", color: "#ea580c",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
            }}
        >
            {icon}
        </div>
        <div style={{ flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>{title}</span>
                <StatusTag status={status} />
            </div>
            <div style={{ color: "#64748b", fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>{children}</div>
        </div>
    </div>
);

/**
 * Màn xin quyền sau khi đăng nhập: thông báo + camera (HLV/quản lý), xin 1 lần duy nhất.
 * Nếu thiết bị đã cho phép thông báo từ trước thì tự đăng ký lại trong im lặng.
 */
const PermissionOnboarding = () => {
    const roles = getRoles() || [];
    const needCamera = roles.some((r) => CAMERA_ROLES.includes(r));
    const iosWeb = isIOS() && !isStandalone();

    const [open, setOpen] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [notifStatus, setNotifStatus] = useState("default");
    const [cameraStatus, setCameraStatus] = useState("prompt");

    const refreshStatuses = useCallback(async () => {
        const notif = isPushSupported() ? getPushPermission() : (iosWeb ? "need-install" : "unsupported");
        const camera = needCamera ? await getCameraPermission() : "granted";
        setNotifStatus(notif);
        setCameraStatus(camera);
        return { notif, camera };
    }, [needCamera, iosWeb]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const { notif, camera } = await refreshStatuses();
            if (cancelled) return;

            // Đã cho phép thông báo trên thiết bị -> đăng ký lại im lặng (đảm bảo server có endpoint mới nhất)
            if (notif === "granted" && !readStorage(sessionStorage, SYNC_KEY)) {
                writeStorage(sessionStorage, SYNC_KEY, "1");
                subscribePush().catch(() => {});
            }

            const canAskNotif = notif === "default";
            const canAskCamera = needCamera && camera === "prompt";
            if (!readStorage(localStorage, DONE_KEY) && (canAskNotif || canAskCamera || notif === "need-install")) {
                setOpen(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [refreshStatuses, needCamera]);

    const finish = () => {
        writeStorage(localStorage, DONE_KEY, "1");
        setOpen(false);
    };

    const handleAllow = async () => {
        setRequesting(true);
        try {
            // 1. Thông báo
            if (isPushSupported() && Notification.permission === "default") {
                await subscribePush().catch(() => {});
            }
            // 2. Camera: mở rồi tắt ngay, chỉ để trình duyệt ghi nhớ quyền
            if (needCamera && navigator.mediaDevices?.getUserMedia) {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                    stream.getTracks().forEach((t) => t.stop());
                } catch {
                    // Người dùng từ chối hoặc máy không có camera
                }
            }
        } finally {
            await refreshStatuses();
            setRequesting(false);
            finish();
        }
    };

    return (
        <Modal
            open={open}
            centered
            closable={false}
            maskClosable={false}
            width={480}
            title={
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <SafetyCertificateOutlined style={{ color: "#ea580c" }} />
                    Cho phép 1MS hoạt động đầy đủ
                </span>
            }
            footer={[
                <Button key="later" onClick={finish} disabled={requesting}>
                    Để sau
                </Button>,
                <Button
                    key="allow"
                    type="primary"
                    loading={requesting}
                    onClick={handleAllow}
                    style={{ background: "#ea580c", borderColor: "#ea580c" }}
                >
                    Cho phép
                </Button>,
            ]}
        >
            <div style={{ color: "#475569", fontSize: 14, marginBottom: 4 }}>
                Để dùng 1MS thuận tiện như một ứng dụng trên điện thoại, hệ thống cần bạn cho phép một số quyền.
                Bạn chỉ cần đồng ý <b>một lần</b>, trình duyệt sẽ ghi nhớ cho những lần sau.
            </div>

            <PermissionItem icon={<BellOutlined />} title="Thông báo" status={notifStatus}>
                <b>Vì sao cần:</b> để bạn nhận kịp các nhắc nhở công việc ngay cả khi không mở app, như ca học chưa điểm danh,
                sinh nhật võ sinh, học phí đến hạn và thông báo quan trọng từ ban quản trị 1MS.
                <br />
                Chỉ gửi thông báo liên quan đến công việc của CLB. Bạn có thể tắt bất cứ lúc nào trong <b>Hồ sơ → Thông báo</b>.
            </PermissionItem>

            {needCamera && (
                <PermissionItem icon={<CameraOutlined />} title="Camera" status={cameraStatus}>
                    <b>Vì sao cần:</b> để <b>điểm danh võ sinh và chấm công HLV bằng khuôn mặt</b>.
                    Cho phép một lần ở đây để không bị hỏi lại mỗi lần điểm danh.
                    <br />
                    Camera <b>chỉ bật khi bạn mở màn điểm danh</b>. Ảnh chỉ dùng để nhận diện người trong CLB của bạn,
                    1MS không quay video hay ghi âm.
                </PermissionItem>
            )}

            {iosWeb && (
                <PermissionItem icon={<MobileOutlined />} title="Cài app ra màn hình chính (iPhone)" status="need-install">
                    Trên iPhone, Safari <b>không hỗ trợ thông báo</b> và <b>hỏi lại quyền camera mỗi lần</b> nếu bạn mở 1MS như trang web thường.
                    Hãy bấm nút <b>Chia sẻ</b> → <b>"Thêm vào MH chính"</b>, rồi mở 1MS từ biểu tượng trên màn hình chính.
                </PermissionItem>
            )}

            {(notifStatus === "denied" || cameraStatus === "denied") && (
                <div style={{ marginTop: 12, fontSize: 12, color: "#b45309", background: "#fffbeb", padding: "8px 12px", borderRadius: 8 }}>
                    Quyền đang bị chặn sẽ không hỏi lại được. Hãy bấm biểu tượng ổ khoá cạnh thanh địa chỉ (hoặc Cài đặt của trình duyệt)
                    → cho phép Thông báo / Camera cho trang 1ms.taekwondo.id.vn.
                </div>
            )}
        </Modal>
    );
};

export default PermissionOnboarding;
